import { useEffect, useState } from 'react';
import type { GameSocketApi } from './hooks/useGameSocket';
import {
  useSnapshot,
  usePrivateState,
  useMyId,
  useAmDrawer,
  useHint,
} from './store/gameStore';
import DrawCanvas from './components/DrawCanvas';
import { GuestBadge } from './components/GuestBadge';
import Chat from './components/Chat';

export default function GameShell({ socket }: { socket: GameSocketApi }) {
  const snapshot = useSnapshot();
  const privateState = usePrivateState();
  const hint = useHint();
  const myId = useMyId();
  const isDrawer = useAmDrawer();

  if (!snapshot) return null;
  const me = snapshot.players.find((p) => p.id === myId);
  if (!me) return null;

  if (snapshot.phase === 'gameover') return <GameOver socket={socket} />;

  const word = isDrawer ? privateState?.yourWord ?? null : null;
  const dots = snapshot.wordLength;
  const progress = <TurnTimer deadline={snapshot.turnDeadline} />;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gradient-to-b from-scr-bg to-[#e9faf3] supports-[height:100dvh]:h-dvh">
      {/* Top bar */}
      <header className="mx-auto flex w-full max-w-7xl shrink-0 flex-wrap items-center gap-x-4 gap-y-2 px-4 pt-4 sm:px-6">
        <span className="font-display text-2xl font-black tracking-tight text-scr-ink">
          Pictionary
        </span>
        <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-scr-ink/60 ring-1 ring-scr-border">
          Round {snapshot.roundIndex + 1} / {snapshot.totalRounds}
        </span>
        <div className="ml-auto flex items-center gap-3">
          {progress}
        </div>
      </header>

      {/* Round banner */}
      <div className="mx-auto w-full max-w-7xl shrink-0 px-4 pt-3 sm:px-6">
        <RoundBanner isDrawer={isDrawer} word={word} dots={dots} hint={hint} />
      </div>

      {/* Board + side panels – left score, center canvas, right chat */}
      <main className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col gap-3 px-4 py-3 sm:px-6 md:flex-row md:gap-2">
        {/* Left players panel – per-player scores, clean like right chat */}
        <div className="flex max-h-[220px] w-full min-h-0 flex-col rounded-2xl border border-scr-border bg-white md:max-h-none md:h-auto md:w-[220px] md:flex-none">
          <div className="flex items-center justify-between border-b border-scr-border px-4 py-2.5">
            <span className="text-xs font-bold uppercase tracking-widest text-scr-ink/50">Players</span>
            <span className="rounded-full bg-scr-bg px-2 py-0.5 text-[10px] font-bold text-scr-ink/60">{snapshot.players.length} / 8</span>
          </div>
          <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-2 py-2">
            {snapshot.players.map((p) => (
              <div key={p.id} className={`flex items-center gap-2 rounded-xl border px-2.5 py-2 ${p.id === snapshot.currentDrawerId ? 'border-amber-200 bg-amber-50' : 'border-scr-border/60 bg-scr-bg/50'}`}>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-black text-white" style={{ backgroundColor: p.color }}>
                  {p.name.slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className="truncate text-xs font-bold leading-none text-scr-ink">{p.name}</p>
                  <p className="truncate text-[10px] font-medium leading-none text-scr-ink/50">
                    {p.guessed && p.id !== snapshot.currentDrawerId ? 'guessed ✓' : p.id === snapshot.currentDrawerId ? 'drawing' : 'guessing'}
                  </p>
                </div>
                {p.kind === 'guest' && <span className="shrink-0"><GuestBadge /></span>}
                <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-black text-scr-ink shadow-sm ring-1 ring-scr-border">{p.score}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Canvas + chat area */}
        <div className="flex-1 flex flex-col gap-2 sm:px-6">
          <div className="flex-1">
            <DrawCanvas socket={socket} />
          </div>
        </div>

        {/* Chat – fixed share on phones, full-stretch sidebar on desktop */}
        <div className="h-[32dvh] min-h-[220px] shrink-0 md:h-auto md:w-[260px] md:min-h-0 md:flex-none xl:w-[280px]">
          <Chat socket={socket} />
        </div>
      </main>
    </div>
  );
}

function RoundBanner({
  isDrawer,
  word,
  dots,
  hint,
}: {
  isDrawer: boolean;
  word: string | null;
  dots: number | null;
  hint: string;
}) {
  const [showWord, setShowWord] = useState(isDrawer);
  useEffect(() => setShowWord(isDrawer), [isDrawer]);

  return (
    <div className="flex flex-col items-center gap-1 rounded-2xl border border-scr-border bg-white px-5 py-3 shadow-sm">
      {isDrawer ? (
        <>
          <p className="text-[11px] font-bold uppercase tracking-widest text-scr-ink/40">
            Your word — draw this without using letters
          </p>
          <p className="text-center font-mono text-3xl font-black tracking-[0.2em] text-scr-accent-dark">
            {showWord ? word : '•'.repeat((word?.length ?? 0) || 1)}
          </p>
        </>
      ) : (
        <>
          <p className="text-[11px] font-bold uppercase tracking-widest text-scr-ink/40">
            Guess the word
          </p>
          <p className="text-center font-mono text-3xl font-black tracking-[0.24em] text-scr-ink">
            {hint ? (
              <HintString hint={hint} />
            ) : (
              dots != null && <span className="text-scr-ink/30">{'•'.repeat(dots)}</span>
            )}
          </p>
        </>
      )}
    </div>
  );
}

/** Renders the progressive hint: revealed letters in accent, masked `_` and
 *  spacing kept dim so the letter count stays obvious. */
function HintString({ hint }: { hint: string }) {
  return (
    <>
      {hint.split('').map((ch, i) =>
        ch === '_' ? (
          <span key={i} className="text-scr-ink/25">
            _
          </span>
        ) : (
          <span key={i} className="text-scr-accent-dark">
            {ch}
          </span>
        ),
      )}
    </>
  );
}

function TurnTimer({ deadline }: { deadline: number | null }) {
  const [left, setLeft] = useState<number | null>(deadline ? deadline - Date.now() : null);

  useEffect(() => {
    setLeft(deadline ? deadline - Date.now() : null);
    if (!deadline) return;
    const id = setInterval(() => {
      const d = deadline - Date.now();
      setLeft(d);
      if (d <= 0) clearInterval(id);
    }, 200);
    return () => clearInterval(id);
  }, [deadline]);

  const secs = left == null ? null : Math.max(0, Math.ceil(left / 1000));
  if (secs == null) return null;
  const urgent = secs <= 10;
  return (
    <span
      className={`rounded-full px-3 py-1 font-mono text-sm font-black ${
        urgent
          ? 'animate-pulse bg-red-100 text-red-600'
          : 'bg-scr-accent/10 text-scr-accent-dark'
      }`}
    >
      {secs}s
    </span>
  );
}

function GameOver({ socket }: { socket: GameSocketApi }) {
  const snapshot = useSnapshot()!;
  const ranked = [...snapshot.players].sort((a, b) => b.score - a.score);
  const winner = ranked[0];

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-scr-bg to-[#e9faf3] px-6 py-12">
      <h1 className="font-display text-5xl font-black tracking-tight text-scr-ink">
        Game over!
      </h1>
      <p className="mt-2 text-sm text-scr-ink/55">
        {winner.name} takes the crown with {winner.score} points.
      </p>

      {/* Podium */}
      <div className="mt-8 w-full max-w-md space-y-2">
        {ranked.map((p, i) => (
          <div
            key={p.id}
            className={`flex items-center gap-3 rounded-2xl border p-4 ${
              i === 0 ? 'border-scr-gold bg-white' : 'border-scr-border bg-white'
            }`}
          >
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-xl text-sm font-black ${
                i === 0 ? 'bg-scr-gold text-white' : 'bg-scr-bg text-scr-ink/50'
              }`}
            >
              {i + 1}
            </span>
            <span
              className="h-8 w-8 rounded-full"
              style={{ backgroundColor: p.color }}
            />
            <span className="min-w-0 flex-1 truncate text-sm font-bold text-scr-ink">
              {p.name}
            </span>
            <span className="font-mono text-lg font-black text-scr-accent-dark">
              {p.score}
            </span>
            {i === 0 && <span className="text-xs">🏆</span>}
          </div>
        ))}
      </div>

      <div className="mt-8 flex gap-3">
        <button
          onClick={() => socket.leave()}
          className="rounded-xl bg-scr-accent px-6 py-3 text-sm font-bold text-white shadow-lg shadow-scr-accent/25 transition hover:brightness-105"
        >
          Play again
        </button>
        <button
          onClick={() => {
            window.location.reload();
          }}
          className="rounded-xl border border-scr-border bg-white px-6 py-3 text-sm font-bold text-scr-ink/60 transition hover:text-scr-ink"
        >
          Home
        </button>
      </div>
    </div>
  );
}